import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { calculateAnalysis } from '../utils';

const TradeCharts = ({ trades, profitTargets }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [timeframe, setTimeframe] = useState('all'); // 'all', '7d', '30d', '90d'
  const initialBalance = profitTargets?.initialBalance || 10000; // Use user's initial balance or default

  // Filter trades based on timeframe
  const filterTradesByTimeframe = (trades, timeframe) => {
    if (timeframe === 'all') return trades;
    
    const now = new Date();
    let startDate = new Date();
    
    switch (timeframe) {
      case '7d':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(now.getDate() - 90);
        break;
      default:
        return trades;
    }
    
    return trades.filter(trade => new Date(trade.closeDate) >= startDate);
  };

  const filteredTrades = filterTradesByTimeframe(trades, timeframe);

  // Recompute analysis based on the filtered trades so indices align with charts
  const analysisFiltered = calculateAnalysis(filteredTrades, initialBalance);

  // Prepare data for P&L trend chart
  const pnlTrendData = [...filteredTrades]
    .map((trade, index) => ({
      date: new Date(trade.closeDate).toLocaleDateString(),
      pnl: trade.pnl,
      cumulativePnl: analysisFiltered.cumulativePnl[index] || 0,
      tradeId: trade.tradeId,
    }));

  // Prepare data for equity curve
  const equityCurveData = filteredTrades.map((trade, index) => ({
    date: new Date(trade.closeDate).toLocaleDateString(),
    equity: initialBalance + (analysisFiltered.cumulativePnl[index] || 0),
    // Dynamic target line "works like P&L": increases by daily target per trade
    targetEquity: initialBalance + (index + 1) * (profitTargets?.profitTarget || 0),
    tradeId: trade.tradeId,
  }));
  
  // Add initial balance point to equity curve data
  equityCurveData.unshift({
    date: 'Initial',
    equity: initialBalance,
    targetEquity: initialBalance,
  });
  
  // Profit target is integrated per-point via targetEquity field in equityCurveData

  // Prepare data for win/loss distribution
  const winLossData = [
    { name: 'Wins', value: analysisFiltered.wins || 0 },
    { name: 'Losses', value: analysisFiltered.losses || 0 },
  ];

  // const COLORS = ['#4caf50', '#f44336']; // Commented out as unused

  // Prepare data for daily P&L chart
  const dailyPnlData = (() => {
    const dailyData = {};
    
    filteredTrades.forEach(trade => {
      const date = new Date(trade.closeDate).toLocaleDateString();
      if (!dailyData[date]) {
        dailyData[date] = { date, pnl: 0, trades: 0 };
      }
      dailyData[date].pnl += trade.pnl;
      dailyData[date].trades += 1;
    });
    
    return Object.values(dailyData);
  })();

  // Prepare data for market condition performance
  const marketConditionData = Object.entries(analysisFiltered.marketConditions || {}).map(
    ([condition, count]) => ({
      name: condition,
      value: count,
    })
  );

  // Prepare data for setup quality performance
  const setupQualityData = Object.entries(analysisFiltered.setupQualities || {}).map(
    ([quality, data]) => ({
      name: quality,
      value: data.winRate,
      count: data.count,
      totalPnl: data.totalPnl,
    })
  );

  // Prepare data for tag performance
  const tagPerformanceData = Object.entries(analysisFiltered.tagPerformance || {})
    .map(([tag, data]) => ({
      name: tag,
      value: data.winRate,
      count: data.count,
      totalPnl: data.totalPnl,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10); // Top 10 tags

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTimeframeChange = (event) => {
    setTimeframe(event.target.value);
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1, boxShadow: 3 }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value.toFixed(2)}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Trade Analytics</Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Timeframe</InputLabel>
          <Select
            value={timeframe}
            label="Timeframe"
            onChange={handleTimeframeChange}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Performance" />
        <Tab label="Distribution" />
        <Tab label="Market Analysis" />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                P&L Trend
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={pnlTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="pnl"
                    name="P&L per Trade"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Equity Curve
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={equityCurveData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="equity"
                    name="Equity"
                    stroke="#4caf50"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  {(profitTargets?.profitTarget || 0) > 0 && (
                    <Line
                      type="monotone"
                      dataKey="targetEquity"
                      name="Profit Target"
                      stroke="#ff9800"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      dot={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Daily P&L
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={dailyPnlData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="pnl"
                    name="Daily P&L"
                    fill="#1976d2"
                  >
                    {dailyPnlData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.pnl >= 0 ? '#4caf50' : '#f44336'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Win/Loss Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={winLossData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {winLossData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.name === 'Wins' ? '#4caf50' : '#f44336'}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Trades']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Setup Quality Performance
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={setupQualityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'value') return [`${value.toFixed(2)}%`, 'Win Rate'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Win Rate (%)" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Tag Performance
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={tagPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'value') return [`${value.toFixed(2)}%`, 'Win Rate'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Win Rate (%)" fill="#4caf50" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Market Condition Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={marketConditionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Number of Trades" fill="#ff9800" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Setup Quality P&L Impact
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={setupQualityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Total P&L']} />
                  <Legend />
                  <Bar dataKey="totalPnl" name="Total P&L" fill="#9c27b0" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default TradeCharts;